import { Children, type FC, Fragment, type ReactNode } from "react";

import { clsx } from "clsx";

import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons/faChevronDown";
import { faExclamation } from "@fortawesome/free-solid-svg-icons/faExclamation";
import { faPaperclip } from "@fortawesome/pro-duotone-svg-icons/faPaperclip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";

// A step's state. Done: a filled teal check. Working: a teal ring around a
// teal centre. Todo: a faint ring. Failed: a rose "!".
export type CaseStatusTone = "done" | "todo" | "working" | "failed";

// `sm` is the cases list's strip; `md` the case page's steps; `chip` an
// `md` dot in the box of an empty case's bubble icon chip (26px).
type CaseStepDotSize = "sm" | "md" | "chip";

const MD = {
  mark: "size-5",
  todo: "size-4",
  centre: "size-2",
  icon: "text-[11px]",
};

const DOT_SIZE: Record<
  CaseStepDotSize,
  { box: string; mark: string; todo: string; centre: string; icon: string }
> = {
  sm: {
    box: "size-4",
    mark: "size-4",
    todo: "size-3",
    centre: "size-1.5",
    icon: "text-[9px]",
  },
  md: { box: "size-5", ...MD },
  chip: { box: "size-[26px]", ...MD },
};

// The dot centred in a fixed box, so a smaller todo ring lines up with the
// rest. Decorative: the step's title or label says its state.
export const CaseStepDot: FC<{
  tone: CaseStatusTone;
  size?: CaseStepDotSize;
}> = ({ tone, size = "sm" }) => (
  <span
    aria-hidden
    className={clsx(
      "flex shrink-0 items-center justify-center",
      DOT_SIZE[size].box,
    )}
  >
    <CaseStepMark tone={tone} size={size} />
  </span>
);

const CaseStepMark: FC<{
  tone: CaseStatusTone;
  size: CaseStepDotSize;
}> = ({ tone, size }) => {
  const dot = DOT_SIZE[size];

  switch (tone) {
    case "done":
      return (
        <span
          className={clsx(
            "flex items-center justify-center rounded-full bg-indigo-600 text-white",
            dot.mark,
            dot.icon,
          )}
        >
          <FontAwesomeIcon icon={faCheck} />
        </span>
      );
    case "working":
      return (
        <span
          className={clsx(
            "flex items-center justify-center rounded-full border-2 border-indigo-600 bg-white",
            dot.mark,
          )}
        >
          <span className={clsx("rounded-full bg-indigo-600", dot.centre)} />
        </span>
      );
    case "failed":
      return (
        <span
          className={clsx(
            "flex items-center justify-center rounded-full bg-rose-700 text-white",
            dot.mark,
            dot.icon,
          )}
        >
          <FontAwesomeIcon icon={faExclamation} />
        </span>
      );
    case "todo":
      return (
        <span
          className={clsx(
            "rounded-full border-2 border-[#cfd6de] bg-white",
            dot.todo,
          )}
        />
      );
  }
};

export type CaseStepTab<K extends string = string> = {
  key: K;
  label: string;
  state: string;
  tone: CaseStatusTone;
  title: string;
};

const STEP_LABEL: Record<CaseStatusTone, string> = {
  done: "font-semibold text-body",
  working: "font-semibold text-body",
  failed: "font-semibold text-rose-700",
  todo: "font-medium text-body",
};

// A step's dot, its name and its state, as the strip and the phone's list
// both show it.
const StepSummary: FC<{ step: CaseStepTab; labelClassName: string }> = ({
  step,
  labelClassName,
}) => (
  <>
    <CaseStepDot tone={step.tone} size="md" />
    <span className="min-w-0">
      <span className={clsx("block truncate text-[13.5px]", labelClassName)}>
        {step.label}
      </span>
      <span className="text-muted block truncate text-[12px]">
        {step.state}
      </span>
    </span>
  </>
);

// The case page's steps, in the frosted header where its tabs were: picking
// one opens its section. Each is a dot, its name and its state, as wide as
// those need, with the lines between stretching to fill the strip; the open
// one is named in navy and underlined in teal. `selected` is null while a section
// that isn't a step (Supporting documents) is open. On a phone the steps fold
// into one button that lists them all.
export const CaseStepTabs = <K extends string>({
  steps,
  selected,
  onSelect,
}: {
  steps: CaseStepTab<K>[];
  selected: K | null;
  onSelect(key: K): void;
}) => {
  const index = steps.findIndex((step) => step.key === selected);
  const current = index >= 0 ? steps[index] : undefined;

  return (
    <>
      <div className="-mx-2 flex max-md:hidden">
        {steps.map((step, i) => {
          const active = step.key === selected;

          return (
            <Fragment key={step.key}>
              {i > 0 && (
                <span
                  aria-hidden
                  className="mx-1 mt-[22px] h-px min-w-6 flex-1 bg-[rgba(18,53,91,0.14)]"
                />
              )}
              <button
                type="button"
                title={step.title}
                aria-current={active ? "step" : undefined}
                onClick={() => onSelect(step.key)}
                className={clsx(
                  "flex min-w-0 cursor-pointer items-start gap-2.5 border-b-2 px-2 pt-3 pb-2.5 text-left",
                  active ? "border-indigo-600" : "border-transparent",
                )}
              >
                <StepSummary
                  step={step}
                  labelClassName={clsx(
                    "leading-5",
                    active ? "text-ink font-semibold" : STEP_LABEL[step.tone],
                  )}
                />
              </button>
            </Fragment>
          );
        })}
      </div>

      <Menu as="div" className="relative pt-1 pb-2.5 md:hidden">
        <MenuButton className="ring-hairline flex w-full cursor-pointer items-center gap-2.5 rounded-xl bg-white px-3 py-2 text-left ring-1">
          {current ? (
            <>
              <CaseStepDot tone={current.tone} size="md" />
              <span className="min-w-0 flex-1">
                <span className="text-ink block truncate text-[13.5px] font-semibold">
                  {current.label}
                </span>
                <span className="text-muted block truncate text-[11.5px]">
                  Step {index + 1} of {steps.length} · {current.state}
                </span>
              </span>
            </>
          ) : (
            <span className="text-ink min-w-0 flex-1 truncate text-[13.5px] font-semibold">
              Supporting documents
            </span>
          )}
          <FontAwesomeIcon
            icon={faChevronDown}
            aria-hidden
            className="text-muted text-xs"
          />
        </MenuButton>
        <MenuItems className="ring-hairline absolute inset-x-0 z-40 mt-1 rounded-xl bg-white p-1 shadow-lg ring-1 focus:outline-none">
          {steps.map((step) => {
            const active = step.key === selected;

            return (
              <MenuItem key={step.key}>
                <button
                  type="button"
                  title={step.title}
                  aria-current={active ? "step" : undefined}
                  onClick={() => onSelect(step.key)}
                  className={clsx(
                    "flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-left",
                    active
                      ? "bg-indigo-50 data-focus:bg-indigo-100"
                      : "data-focus:bg-slate-50",
                  )}
                >
                  <StepSummary
                    step={step}
                    labelClassName={
                      active
                        ? "font-semibold text-indigo-700"
                        : STEP_LABEL[step.tone]
                    }
                  />
                </button>
              </MenuItem>
            );
          })}
        </MenuItems>
      </Menu>
    </>
  );
};

// Supporting documents in the case header: a quiet text link, not a step, as
// it has no state. On a phone just the paperclip, still named for readers,
// padded to a 44px tap target.
export const CaseSupportingLink: FC<{
  open: boolean;
  onClick(): void;
  children: ReactNode;
}> = ({ open, onClick, children }) => (
  <button
    type="button"
    aria-pressed={open}
    onClick={onClick}
    className={clsx(
      "cursor-pointer text-[12.5px] whitespace-nowrap hover:text-indigo-700 hover:underline max-md:-my-3.5 max-md:p-3.5",
      open ? "text-ink font-semibold" : "text-muted",
    )}
  >
    <FontAwesomeIcon icon={faPaperclip} aria-hidden />{" "}
    <span className="max-md:sr-only">{children}</span>
  </button>
);

// An empty case's bubbles, one per step in step order: one row at xl with a
// thin line between each, level with the dots (the bubble's 14px top padding
// plus half its 26px chip). Below xl they wrap as a grid and the lines go.
export const CaseStepBubbles: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <div
    role="tablist"
    aria-label={label}
    className={clsx(
      "grid grid-cols-1 gap-3 xl:flex xl:gap-0 xl:[&>button]:min-w-0 xl:[&>button]:flex-1",
      Children.count(children) === 4 ? "md:grid-cols-4" : "md:grid-cols-3",
    )}
  >
    {Children.map(children, (bubble, index) => (
      <>
        {index > 0 && (
          <span
            aria-hidden
            className="mt-[27px] hidden h-px w-3.5 shrink-0 bg-[rgba(18,53,91,0.14)] xl:block"
          />
        )}
        {bubble}
      </>
    ))}
  </div>
);
